import { afterNextRender, Directive, ElementRef, inject, input, Renderer2 } from '@angular/core';

/**
 * A directive that transforms elements with a `routerLink` into external links if the URL starts with `http`.
 * The directive dynamically creates an `<a>` element with appropriate attributes (`href`, `target`, and `rel`)
 * and replaces the host element while preserving its original attributes and children.
 *
 * @example
 * ```html
 * <div externalLink [routerLink]="'https://example.com'" class="custom-class">
 *   Click here
 * </div>
 * ```
 * The above snippet will dynamically transform the `<div>` into:
 * ```html
 * <a href="https://example.com" target="_blank" rel="noopener noreferrer" class="custom-class">
 *   Click here
 * </a>
 * ```
 *
 * @remarks
 * This directive is useful for automatically converting a component or element into a safe and semantically correct external link.
 * Attributes like `class` from the host element are preserved on the generated `<a>` element. The directive also ensures
 * better security practices by setting the `rel="noopener noreferrer"` attribute when opening links in a new tab.
 *
 * ## Limitations
 * - This directive only processes links where the `routerLink` input starts with `http`.
 * - Attributes explicitly set to `routerLink` or dynamic bindings like `dynamicUrl` are ignored.
 *
 * @directive
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a
 */
@Directive({
  selector: '[externalLink]',
  standalone: true,
})
export class ExternalLinkDirective {
  readonly routerLink = input.required<string>();
  private readonly renderer = inject(Renderer2);
  private readonly el = inject<ElementRef<Element>>(ElementRef);

  constructor() {
    afterNextRender(() => {
      const routerLink = this.routerLink();
      if (routerLink?.startsWith('http')) {
        const host = this.el.nativeElement as HTMLElement;

        const link = this.renderer.createElement('a') as HTMLAnchorElement;
        link.setAttribute('href', routerLink);
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
        link.className = host.className;

        for (const attr of Array.from(host.attributes)) {
          const name = attr.name.toLowerCase();
          if (name === 'class' || name === 'routerlink' || name === 'dynamicurl') continue;
          if (name === 'href' || name === 'target' || name === 'rel') continue;
          link.setAttribute(attr.name, attr.value);
        }

        while (host.firstChild) {
          link.appendChild(host.firstChild);
        }

        host.replaceWith(link);
      }
    });
  }
}
